/**
 * ParkedBadge — the "desk is aware" marker on a parked draft.
 *
 * Shown on the orders list row and in the order modal header so anyone
 * (Laura, a colleague covering the desk) sees why a draft is sitting
 * un-keyed. The full note and who/when ride the tooltip; the modal shows
 * them in full in ParkedBanner.
 */
import { PARK_REASON_LABELS, type OrderRecord } from '../../types/orders';

interface ParkedBadgeProps {
  order: Pick<OrderRecord, 'id' | 'parked_reason' | 'parked_note' | 'parked_by' | 'parked_at' | 'parked_until'>;
}

/** "10/2" from an ISO date without a timezone shift. */
export function formatIsoDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return Number.isFinite(m) && Number.isFinite(d) ? `${m}/${d}` : iso;
}

export function formatParkedBy(order: ParkedBadgeProps['order']): string {
  const who = (order.parked_by || 'the desk').split('@')[0];
  if (!order.parked_at) return who;
  const at = new Date(order.parked_at);
  if (Number.isNaN(at.getTime())) return who;
  return `${who}, ${at.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}`;
}

export default function ParkedBadge({ order }: ParkedBadgeProps) {
  if (!order.parked_reason) return null;
  const label = PARK_REASON_LABELS[order.parked_reason] ?? order.parked_reason;
  const until = order.parked_until ? `, come back ${formatIsoDate(order.parked_until)}` : '';
  const title = `${label} (${formatParkedBy(order)}${until})${order.parked_note ? `: ${order.parked_note}` : ''}`;
  return (
    <span
      data-testid={`parked-badge-${order.id}`}
      title={title}
      style={{
        background: '#ede9fe',
        color: '#5b21b6',
        border: '1px solid #c4b5fd',
        borderRadius: '4px',
        padding: '1px 6px',
        fontSize: '10px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        letterSpacing: '0.02em',
      }}
    >
      PARKED · {label}
    </span>
  );
}
